<template>
  <VDropdown
    v-model:shown="isMenuShown"
    :aria-id="ariaId"
    :distance="30"
    placement="bottom-end"
  >
    <button
      type="button"
      :class="[
        'font-[400] flex items-center text-primary-black',
        { '!text-error-red': props.error }
      ]"
    >
      <div class="mr-[10px]">
        <span
          :class="[`fi fi-${currentCountry} fis rounded-full w-4 h-4 mr-1`]"
        />{{ value }}
      </div>
      <Icon name="chevron-down" />
    </button>
    <template #popper="{ hide }">
      <Transition appear @after-enter="afterEnter">
        <div
          :class="[
            'sticky top-0 z-[2] bg-primary-white inline-block border-[1px] border-border-divider rounded-2xl px-4 py-3 space-x-4 whitespace-nowrap max-h-[48px]',
            `after:content-[''] after:border-[0px] after:absolute after:inset-[-1px] after:rounded-[15px] after:border-primary-tangerine after:pointer-events-none`,
            `after:transition-all after:ease-in-out`,
            `after:hover:border-[3px]`,
            { 'after:!border-[3px]': focus },
            { 'bg-gray-10': !!q }
          ]"
          @click="() => searchPhoneSelectorInputRef?.focus()"
        >
          <input
            ref="searchPhoneSelectorInputRef"
            v-model="q"
            :class="[
              'text-black font-normal placeholder:text-border-divider bg-transparent outline-none w-[88px]'
            ]"
            @blur="handleOnBlur"
            @focus="focus = true"
          />
        </div>
      </Transition>
      <button
        v-for="phone in filteredCountryPhones"
        :key="phone.c"
        type="button"
        :class="[
          'flex items-center cursor-pointer px-4 py-3 rounded-xl w-full text-primary-black ',
          'hover:bg-primary-tangerine hover:text-primary-white',
          { 'bg-primary-tangerine text-primary-white': phone.n == value }
        ]"
        @click="
          () => {
            handleSelect(phone.n)
            hide()
          }
        "
      >
        <span
          :class="[
            `fi fi-${phone.c?.toLocaleLowerCase()} fis rounded-full w-4 h-4 mr-1`
          ]"
        />
        <div class="font-[400]">
          {{ phone.n }}
        </div>
      </button>
    </template>
  </VDropdown>
</template>

<script setup lang="ts">
import countryPhones from '~/utils/country-phones'
import '/node_modules/flag-icons/css/flag-icons.min.css'
import { filter } from 'lodash-es'

interface PhoneSelectorProps {
  error?: boolean
}

const props = defineProps<PhoneSelectorProps>()
const emits = defineEmits<{
  (e: 'change', value: string): void
}>()

const ariaId = useId()
const isMenuShown = ref(false)
const value = ref('+66')
const q = ref('')
const searchPhoneSelectorInputRef = ref<HTMLInputElement | null>(null)
const focus = ref(false)

const afterEnter = () => {
  setTimeout(() => {
    searchPhoneSelectorInputRef.value?.focus()
  }, 200)
}

const handleOnBlur = (_focusEvent: FocusEvent) => {
  focus.value = false
}

const currentCountry = computed(() =>
  countryPhones.find((phone) => phone.n == value.value)?.c.toLocaleLowerCase()
)

const filteredCountryPhones = computed(() =>
  filter(countryPhones, (item) =>
    item.n.toLocaleLowerCase().includes(q.value.toLocaleLowerCase())
  )
)

const handleSelect = (phone: string) => {
  q.value = ''
  value.value = phone
  emits('change', phone)
}
</script>

<style>
.v-popper__arrow-container {
  display: none;
}

.v-popper__inner {
  padding: 8px;
  border-radius: 16px !important;
  overflow: hidden;
}

.v-popper__inner > div {
  overflow-y: scroll;
  max-height: 320px;
  border-radius: 16px !important;
}

.v-popper__inner > div::-webkit-scrollbar {
  display: none;
}
</style>
